import { SyntheticEvent, useMemo } from 'react';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import { useField } from 'react-final-form';
import { Popper, TextField, styled } from '@mui/material';

/**
 * COMPONENTE
 * Adapter avanzato per integrare Material-UI Autocomplete con React Final Form
 *
 * Funzionalità principali:
 * - Gestione valori semplici (ID) o oggetti completi
 * - Auto-selection
 * - Styling custom del Popper
 * - Integrazione completa con validazione Final Form
 *
 * @param props - Configurazione del componente Autocomplete
 * @param props.input - Oggetto input di React Final Form (contiene name)
 * @param props.options - Array di opzioni disponibili per l'autocomplete
 * @param props.optionLabel - Key dell'oggetto opzione da usare come label visualizzata
 * @param props.optionValue - Key dell'oggetto opzione da usare come valore salvato
 * @param props.submitObject - Se true, salva l'intero oggetto invece del solo valore
 * @param props.freeSolo - Se true, permette input libero (non solo dalle options)
 * @param props.disabled - Disabilita completamente il componente
 * @param props.autoSelect - Abilita auto-selection quando c'è una sola option
 * @param props.rest - Altre props passate al TextField (label, placeholder, etc.)
 */

export const AutocompleteDialogAdapter = ({
  input: { name },
  options = [],
  optionLabel,
  optionValue,
  submitObject,
  freeSolo,
  disabled,
  autoSelect = true,
  ...rest
}: any) => {
  const { input, meta } = useField(name);

  const selectedOption = useMemo(() => {
    if (submitObject) {
      return input.value ?? null;
    }
    const value = input.value;
    if (value === undefined || value === null || value === '') return null;
    return options.find((o: any) => o?.[optionValue] === value) ?? null;
  }, [input.value, options, optionValue, submitObject]);

  const handleDisabled = () => {
    if (freeSolo === true) return false;
    return !options.length;
  };

  const onChange = (_e: SyntheticEvent, option: any) => {
    if (!option) {
      input.onChange('');
      return;
    }
    if (submitObject === true) input.onChange(option);
    else input.onChange(typeof option === 'string' ? option : option[optionValue] ?? '');
  };

  // Auto-select SOLO se il campo è vuoto e c’è una sola option
  const shouldAutoSelect =
    autoSelect !== false &&
    (input.value === undefined || input.value === null || input.value === '') &&
    options.length === 1;

  if (shouldAutoSelect) {
    // fai set solo se ancora vuoto
    const only = options[0];
    // setta valore nel form
    if (submitObject) input.onChange(only);
    else input.onChange(only?.[optionValue] ?? '');
  }

  const StyledPopper = styled(Popper)({
    [`& .${autocompleteClasses.listbox}`]: {
      boxSizing: 'border-box',
      '& ul': { padding: 0, margin: 0 }
    }
  });

  const showError = Boolean(meta?.touched && meta?.error);

  return (
    <Autocomplete
      PopperComponent={StyledPopper}
      options={options}
      disabled={disabled ? true : handleDisabled()}
      getOptionLabel={(option: any) => option?.[optionLabel] ?? ''}
      isOptionEqualToValue={(option: any, value: any) =>
        option?.[optionValue] === value?.[optionValue]
      }
      value={selectedOption}
      onChange={onChange}
      renderInput={(params) => (
        <TextField
          {...params}
          {...rest}
          fullWidth
          error={showError}
          helperText={showError ? meta.error : ''}
        />
      )}
    />
  );
};
